import {
    Box,
    Button,
    MenuItem,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "config";
import usePersonList from "hooks/usePersonList";
import React, { useEffect, useMemo, useState } from "react";
import employeeResource from "features/maintenance/resources/employeeResource";
import DTextField from "components/common/DTextField";
import SelectDateField from "components/common/SelectDataField";
import employmentStatusResource from "features/maintenance/resources/employmentStatusResource";
import DGridAg from "components/common/DGridAg";
import employeeColumnDefs from "../config/employeeColumnDefs";
import GridContainer from "components/common/GridContainer";
import GridItem from "components/common/GridItem";
import { makeStyles } from "@mui/styles";
import { Add, Refresh } from "@mui/icons-material";
import { setValue } from "features/employee/dataStore/employeeSlice";
import { useDispatch, useSelector } from "react-redux";
import MAINTENANCE_STORE_SELECTORS from "features/maintenance/dataStore/selectors";

const styles = {
    card: {
        marginBottom: "16px"
    }
};

const useStyles = makeStyles(styles);

export default function EmployeeListPage() {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const classes = useStyles();

    const dispatch = useDispatch();

    const { items, loading, error, refresh } = usePersonList();

    const profile = useSelector((state) => state.user.profile);

    const employmentStatuses = useSelector(
        (state) =>
            state.maintenance[MAINTENANCE_STORE_SELECTORS.EMPLOYMENT_STATUS]
    );

    useEffect(() => {
        dispatch(setValue({ field: "list", value: items }));
    }, [items]);

    const handleNew = () => {
        navigate(ROUTES.EMPLOYEES_NEW);
    };

    const statusOptions = useMemo(() => {
        return [
            ...new Set(
                items
                    .map((employee) => employee.employmentStatusName)
                    .filter(Boolean)
            )
        ];
    }, [items]);

    const handleEdit = (employee) => {
        navigate(ROUTES.EMPLOYEE(employee.id));
    };

    const filteredItems = useMemo(() => {
        const query = search.trim().toLowerCase();

        return items.filter((employee) => {
            const matchesSearch =
                !query ||
                employeeResource.searchFields.some((field) => {
                    const value = field
                        .split(".")
                        .reduce((current, key) => current?.[key], employee);

                    return String(value ?? "")
                        .toLowerCase()
                        .includes(query);
                });

            const matchesStatus =
                !statusFilter || employee.employmentStatusName === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [items, search, statusFilter]);

    // const hasEmployees = items.length > 0;
    // const hasFilteredEmployees = filteredItems.length > 0;
    const hasFilters = search.trim() !== "" || !!statusFilter;

    return (
        <Box>
            {/* <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ mb: 2 }}
            >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <DTextField
                        value={search}
                        onChange={(event) => {
                            setSearch(event.target.value);
                        }}
                        placeholder="Search employees..."
                        size="small"
                        sx={{ width: { xs: "100%", sm: 320 } }}
                        label="Search"
                    />

                    <SelectDateField
                        fullWidth
                        label="Employment Status"
                        value={statusFilter}
                        required
                        resource={employmentStatusResource}
                        valueField="description"
                        displayField="description"
                        sourceDataField="description"
                        targetDataField="employmentStatusId"
                        addEmpty
                        emptyLabel="Select All"
                        onChange={(value) => {
                            setStatusFilter(value.description || "");
                        }}
                    />

                </Stack>

                <Button variant="contained" onClick={handleNew}>
                    New Employee
                </Button>
            </Stack>*/}
            <GridContainer
                alignItems="flex-end"
                spacing={1}
                card
                cardClassName={classes.card}
            >
                <GridItem xs={3}>
                    <DTextField
                        fullWidth
                        value={search}
                        onChange={(event) => {
                            setSearch(event.target.value);
                        }}
                        placeholder="Search employees..."
                        size="small"
                        sx={{ width: { xs: "100%" } }}
                        label="Search"
                    />
                </GridItem>
                <GridItem xs={2}>
                    <SelectDateField
                        fullWidth
                        label="Employment Status"
                        value={statusFilter}
                        resource={employmentStatusResource}
                        valueField="description"
                        displayField="description"
                        sourceDataField="description"
                        targetDataField="employmentStatusId"
                        addEmpty
                        emptyLabel="Select All"
                        onChange={(value) => {
                            setStatusFilter(value.description || "");
                        }}
                        defaultOptions={employmentStatuses}
                    />
                </GridItem>
                <GridItem xs />
                <GridItem>
                    <Button variant="contained" onClick={handleNew}>
                        <Add /> New Employee
                    </Button>
                </GridItem>
                <GridItem>
                    <Button
                        variant="contained"
                        onClick={() => refresh({ cancelRefetch: false })}
                        disabled={loading}
                        loading={loading}
                    >
                        <Refresh />
                    </Button>
                </GridItem>
            </GridContainer>

            {loading && <Typography>Loading employees...</Typography>}

            {!loading && error && (
                <Typography color="error">{error}</Typography>
            )}

            {!loading && !error && (
                <Typography>
                    {/*<EmployeeTable
                        rows={filteredItems}
                        onSelect={(employee) => {
                            navigate(ROUTES.EMPLOYEE(employee.id));
                        }}
                    />*/}
                    <DGridAg
                        rowData={filteredItems}
                        columnDefs={employeeColumnDefs({
                            onEdit: handleEdit,
                            linkQuery: (employee) =>
                                ROUTES.EMPLOYEE(employee.id),
                            currentPersonId: Number.parseInt(
                                profile?.personId,
                                10
                            )
                        })}
                        height="calc(100vh - 255px)"
                        rowHeight={50}
                        loading={loading}
                        overlayNoRowsTemplate={
                            hasFilters
                                ? "No employees found. Try adjusting your search."
                                : "No employees found."
                        }
                        suppressCellFocus={true}
                        // domLayout="autoHeight"
                    />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                    >
                        {search.trim()
                            ? `${filteredItems.length} of ${items.length} employees`
                            : `${items.length} employee${
                                  items.length !== 1 ? "s" : ""
                              }`}
                    </Typography>
                </Typography>
            )}
        </Box>
    );
}
